// shared/api/chat.js
// Chat API - AI chat message functions
import { supabase } from '../supabase';

/**
 * Save a chat message
 * @param {Object} messageData - {session_id, role, content, user_id}
 * @returns {Promise<{message, error}>}
 */
export const saveChatMessage = async (messageData) => {
  try {
    const { data, error } = await supabase
      .from('chat_messages')
      .insert({
        ...messageData,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return { message: data, error: null };
  } catch (error) {
    console.error('Error saving chat message:', error);
    return { message: null, error };
  }
};

/**
 * Fetch chat messages for a session
 * @param {string} sessionId 
 * @returns {Promise<{messages, error}>}
 */
export const fetchChatMessages = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return { messages: data || [], error: null };
  } catch (error) {
    console.error('Error fetching chat messages:', error);
    return { messages: [], error };
  }
};

/**
 * Delete chat messages for a session
 * @param {string} sessionId 
 * @returns {Promise<{success, error}>}
 */
export const deleteChatMessages = async (sessionId) => {
  try {
    const { error } = await supabase
      .from('chat_messages')
      .delete()
      .eq('session_id', sessionId);

    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting chat messages:', error);
    return { success: false, error };
  }
};

/**
 * Get message count for a session
 * @param {string} sessionId 
 * @returns {Promise<{count, error}>}
 */
export const getChatMessageCount = async (sessionId) => {
  try {
    const { count, error } = await supabase
      .from('chat_messages')
      .select('*', { count: 'exact', head: true })
      .eq('session_id', sessionId);

    if (error) throw error;
    return { count: count || 0, error: null };
  } catch (error) {
    console.error('Error getting message count:', error);
    return { count: 0, error };
  }
};
